import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { darkTheme } from "../../utils/theme";
import useLanguage from "../../hooks/useLanguage";
import useContent from "../../hooks/useContent";
import useToaster from "../../hooks/useToaster";
import useOverlayTemplates from "../../hooks/useOverlayTemplates";
import useMediaQuery from "../../hooks/useMediaQuery";
import {
  EditorRoot,
  LoadingOverlay,
  MainArea,
} from "./style";
import EditorToolbar from "./components/EditorToolbar";
import VideoCanvas from "./components/VideoCanvas";
import VerticalPreview from "./components/VerticalPreview";
import SceneTimeline from "./components/SceneTimeline";
import ShortcutsDialog from "./components/ShortcutsDialog";
import OverlayPicker from "../../components/overlayPicker";
import splitSceneAt from "./splitScene";

// ----- helpers -----

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function isContiguous(indicesArr) {
  if (!indicesArr || indicesArr.length < 2) return false;
  const sorted = [...indicesArr].sort((a, b) => a - b);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] !== sorted[i - 1] + 1) return false;
  }
  return true;
}

function buildDefaultCrop(sourceWidth, sourceHeight) {
  let cropH = sourceHeight;
  let cropW = Math.round(cropH * (9 / 16));
  if (cropW >= sourceWidth) {
    cropW = Math.round(sourceWidth * 0.8);
    cropH = Math.round(cropW * (16 / 9));
  }
  const x = Math.max(0, Math.round((sourceWidth - cropW) / 2));
  const y = Math.max(0, Math.round((sourceHeight - cropH) / 2));
  return { x, y, width: cropW, height: cropH };
}

const HISTORY_LIMIT = 50;
// Crop-drag mutations re-fire many times per second; coalesce them into a
// single undo entry while the same scene's crop is being edited.
const HISTORY_COALESCE_MS = 500;
// Source material is 25fps Israeli broadcast.
const FRAME_STEP = 1 / 25;
const NUDGE_FAST_PX = 10;

// Scene bounds must sit ON a frame time. A who-when split lands on an arbitrary
// Gemini millisecond, and seeking to a mid-frame start_time shows the PREVIOUS
// scene's last frame (the renderer starts the scene at the next frame instead).
// Matches reframer/render_from_json.py `_snap`.
const snapToFrame = (t) => Math.round(Math.round((t ?? 0) / FRAME_STEP) * FRAME_STEP * 1e6) / 1e6;

export default function ReframeEditorPage() {
  const [{ pages: { reframeEditor: text } }] = useLanguage();
  const {
    getContentById,
    getReframeEditData,
    updateReframeEditData,
    reRenderReframe,
    applyOverlayOnly,
  } = useContent();
  const { addToaster } = useToaster();

  // Parse content ID from URL: /reframe-editor/:id
  const contentId = useMemo(() => {
    const parts = window.location.pathname.split("/");
    const idx = parts.findIndex((p) => p === "reframe-editor" || p === "reframe");
    return idx !== -1 ? parts[idx + 1] : parts[parts.length - 1];
  }, []);

  const videoRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [scenes, setScenes] = useState([]);
  const [sourceVideoSize, setSourceVideoSize] = useState({ width: 1920, height: 1080 });
  const [sourceUrl, setSourceUrl] = useState(null);
  const [selectedSceneIndex, setSelectedSceneIndex] = useState(0);
  const [selectedSceneIndices, setSelectedSceneIndices] = useState(() => new Set([0]));
  const [noEditData, setNoEditData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [totalDuration, setTotalDuration] = useState(0);

  const [clipboardCrop, setClipboardCrop] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [overlay, setOverlay] = useState({ enabled: false, payload: null, valid: true });
  const [overlayDrawerOpen, setOverlayDrawerOpen] = useState(false);
  const [overlayTemplate, setOverlayTemplate] = useState(null);
  const isMobile = useMediaQuery();

  const { get: getOverlayTemplate } = useOverlayTemplates();

  // Fetch the full template (with logo URLs) whenever the picker selects one
  useEffect(() => {
    const templateId = overlay.payload?.templateId;
    if (!templateId) { setOverlayTemplate(null); return; }
    getOverlayTemplate(templateId)
      .then((tpl) => setOverlayTemplate(tpl))
      .catch(() => setOverlayTemplate(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay.payload?.templateId]);

  // ---- refs for things we need inside long-lived listeners ----
  const scenesRef = useRef(scenes);
  useEffect(() => { scenesRef.current = scenes; }, [scenes]);

  const selectionRef = useRef({ primary: 0, multi: new Set([0]) });
  useEffect(() => {
    selectionRef.current = { primary: selectedSceneIndex, multi: selectedSceneIndices };
  }, [selectedSceneIndex, selectedSceneIndices]);

  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const lastHistoryRef = useRef({ at: 0, key: null });

  // Original scenes from server — used for "reset to AI default" so users can
  // recover the AI's initial decision per scene after editing.
  const originalScenesRef = useRef([]);

  // ---- Load data ----

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);

        let editData = null;
        try {
          editData = await getReframeEditData(contentId);
        } catch (_) {
          setNoEditData(true);
        }

        if (editData && editData.scenes) {
          const sw = editData.source_width;
          const sh = editData.source_height;
          const next = deepClone(editData.scenes).map((raw) => {
            let scene = {
              ...raw,
              start_time: snapToFrame(raw.start_time),
              end_time: snapToFrame(raw.end_time),
            };
            if (!scene.crop && scene.speaker_segments?.length) {
              scene = { ...scene, crop: scene.speaker_segments[0].crop };
            }
            if (!scene.crop || !sw || !sh) return scene;
            const { width, height } = scene.crop;
            if (width >= sw * 0.98 && height >= sh * 0.98) {
              return { ...scene, crop: buildDefaultCrop(sw, sh) };
            }
            if (scene.strategy === "focus" && scene.trajectory?.length) {
              const t0 = scene.trajectory[0];
              scene = { ...scene, crop: { ...scene.crop, x: t0.x, y: t0.y } };
            }
            return scene;
          });
          setScenes(next);
          originalScenesRef.current = deepClone(next);
          if (editData.source_video_url) setSourceUrl(editData.source_video_url);
          if (sw) setSourceVideoSize({ width: sw, height: sh });
        } else {
          setNoEditData(true);
          const content = await getContentById(contentId);
          setSourceUrl(content?.sourceVideo || content?.banner || null);

          const sw = content?.metadata?.source_width || 1920;
          const sh = content?.metadata?.source_height || 1080;
          setSourceVideoSize({ width: sw, height: sh });

          const defaultCrop = buildDefaultCrop(sw, sh);
          const seed = [
            {
              strategy: "focus",
              start_time: 0,
              end_time: content?.metadata?.duration || 60,
              crop: defaultCrop,
              face_track: true,
            },
          ];
          setScenes(seed);
          originalScenesRef.current = deepClone(seed);
        }
      } catch (err) {
        addToaster({ message: text.errors.loadFailed, severity: "error" });
      } finally {
        setLoading(false);
      }
    }
    if (contentId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId]);

  // Seek main video to the selected scene's start so both VideoCanvas and VerticalPreview
  // show the correct frame when the user clicks a different scene while paused.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || selectedSceneIndex === null || !scenes.length) return;
    const scene = scenes[selectedSceneIndex];
    if (!scene || !video.paused) return;
    video.currentTime = scene.start_time ?? 0;
  }, [selectedSceneIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const didInitialSeekRef = useRef(false);
  useEffect(() => {
    if (didInitialSeekRef.current || !scenes.length || !totalDuration) return;
    const video = videoRef.current;
    if (!video || selectedSceneIndex === null) return;
    const scene = scenes[selectedSceneIndex];
    if (!scene) return;
    didInitialSeekRef.current = true;
    if (video.paused) video.currentTime = scene.start_time ?? 0;
  }, [scenes, totalDuration]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clamp selection if scene count shrinks (e.g. after undo or merge).
  useEffect(() => {
    if (!scenes.length) return;
    if (selectedSceneIndex == null || selectedSceneIndex >= scenes.length) {
      const safe = scenes.length - 1;
      setSelectedSceneIndex(safe);
      setSelectedSceneIndices(new Set([safe]));
      return;
    }
    if (selectedSceneIndices.size > 0) {
      const filtered = [...selectedSceneIndices].filter((i) => i < scenes.length);
      if (filtered.length !== selectedSceneIndices.size) {
        setSelectedSceneIndices(new Set(filtered.length ? filtered : [selectedSceneIndex]));
      }
    }
  }, [scenes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Derived ----

  const selectedScene = useMemo(
    () => (scenes.length > 0 && selectedSceneIndex !== null ? scenes[selectedSceneIndex] : null),
    [scenes, selectedSceneIndex]
  );

  const cropRect = useMemo(() => {
    if (!selectedScene) return null;
    if (selectedScene.crop) return selectedScene.crop;
    return buildDefaultCrop(sourceVideoSize.width, sourceVideoSize.height);
  }, [selectedScene, sourceVideoSize]);

  // ---- History ----

  const pushHistory = useCallback((coalesceKey = null) => {
    const now = Date.now();
    if (
      coalesceKey
      && lastHistoryRef.current.key === coalesceKey
      && now - lastHistoryRef.current.at < HISTORY_COALESCE_MS
    ) {
      lastHistoryRef.current.at = now;
      return;
    }
    lastHistoryRef.current = { at: now, key: coalesceKey };
    undoStackRef.current.push(deepClone(scenesRef.current));
    if (undoStackRef.current.length > HISTORY_LIMIT) undoStackRef.current.shift();
    redoStackRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
  }, []);

  const undo = useCallback(() => {
    if (!undoStackRef.current.length) return;
    const prev = undoStackRef.current.pop();
    redoStackRef.current.push(deepClone(scenesRef.current));
    if (redoStackRef.current.length > HISTORY_LIMIT) redoStackRef.current.shift();
    lastHistoryRef.current = { at: 0, key: null };
    setScenes(prev);
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
  }, []);

  const redo = useCallback(() => {
    if (!redoStackRef.current.length) return;
    const next = redoStackRef.current.pop();
    undoStackRef.current.push(deepClone(scenesRef.current));
    if (undoStackRef.current.length > HISTORY_LIMIT) undoStackRef.current.shift();
    lastHistoryRef.current = { at: 0, key: null };
    setScenes(next);
    setCanUndo(true);
    setCanRedo(redoStackRef.current.length > 0);
  }, []);

  // ---- Selection ----

  const handleSelectScene = useCallback((i, mode = "single") => {
    if (mode === "shift") {
      const anchor = selectionRef.current.primary;
      if (anchor == null) {
        setSelectedSceneIndex(i);
        setSelectedSceneIndices(new Set([i]));
        return;
      }
      const a = Math.min(anchor, i);
      const b = Math.max(anchor, i);
      const range = new Set();
      for (let k = a; k <= b; k++) range.add(k);
      setSelectedSceneIndices(range);
      return;
    }
    if (mode === "toggle") {
      setSelectedSceneIndices((prev) => {
        const next = new Set(prev);
        if (next.has(i)) {
          if (next.size > 1) next.delete(i);
        } else {
          next.add(i);
        }
        if (next.size === 0) next.add(i);
        return next;
      });
      setSelectedSceneIndex((prev) => (prev == null ? i : prev));
      return;
    }
    setSelectedSceneIndex(i);
    setSelectedSceneIndices(new Set([i]));
  }, []);

  // ---- Crop / strategy / face-track edits ----

  const handleCropChange = useCallback((newCrop) => {
    if (selectedSceneIndex === null) return;
    pushHistory(`crop_${selectedSceneIndex}`);
    setScenes((prev) => {
      const scene = prev[selectedSceneIndex];
      if (!scene) return prev;
      let trajectory = scene.trajectory ?? null;
      let speakerSegments = scene.speaker_segments ?? null;
      let strategy = scene.strategy;
      let faceTrack = scene.face_track ?? false;

      if (strategy === "bouncing") {
        // Bouncing renders from speaker_segments, so a manual crop drag would
        // otherwise have no effect. Treat the drag as an override → static.
        strategy = "static";
        speakerSegments = null;
        faceTrack = false;
      } else if (trajectory && scene.crop) {
        // Focus: shift the AI trajectory by the same delta so tracking stays
        // intact at the new crop position.
        const dx = newCrop.x - scene.crop.x;
        const dy = newCrop.y - scene.crop.y;
        if (dx !== 0 || dy !== 0) {
          trajectory = trajectory.map((p) => ({ ...p, x: p.x + dx, y: p.y + dy }));
        }
      }

      // Shallow-clone scenes; deep-clone only the affected scene. Avoids
      // duplicating every other scene's trajectory/speaker arrays per move.
      const next = prev.slice();
      next[selectedSceneIndex] = {
        ...scene,
        crop: newCrop,
        strategy,
        trajectory,
        speaker_segments: speakerSegments,
        face_track: faceTrack,
      };
      return next;
    });
  }, [selectedSceneIndex, pushHistory]);

  const handleSourceSizeLoad = useCallback((w, h) => {
    setSourceVideoSize((prev) => {
      if (prev.width === w && prev.height === h) return prev;
      return { width: w, height: h };
    });
    setScenes((prev) => {
      if (!prev.length) return prev;
      const first = prev[0];
      if (first.strategy !== "focus" || first.start_time !== 0) return prev;
      const newCrop = buildDefaultCrop(w, h);
      const next = deepClone(prev);
      next[0] = { ...next[0], crop: newCrop };
      return next;
    });
  }, []);

  // ---- Split / merge ----

  const handleSplitAt = useCallback((sceneIdx, rawTime) => {
    const list = scenesRef.current;
    if (!list?.length || sceneIdx == null) return;
    const scene = list[sceneIdx];
    if (!scene) return;
    // Every split path funnels here (playhead, timeline menu, midpoint fallback).
    const time = snapToFrame(rawTime);
    const start = scene.start_time ?? 0;
    const end = scene.end_time ?? totalDuration;
    if (time <= start || time >= end) {
      addToaster({ message: text.errors.splitOutOfRange, severity: "warning" });
      return;
    }
    pushHistory();
    setScenes((prev) => {
      const next = [...prev];
      next.splice(sceneIdx, 1, ...splitSceneAt(prev[sceneIdx], time));
      return next;
    });
    setSelectedSceneIndex(sceneIdx);
    setSelectedSceneIndices(new Set([sceneIdx]));
    addToaster({ message: text.toaster.sceneSplit, severity: "success" });
  }, [totalDuration, addToaster, text, pushHistory]);

  const handleSplitAtPlayhead = useCallback(() => {
    const v = videoRef.current;
    if (!v || !scenesRef.current.length) return;
    const t = v.currentTime;
    const list = scenesRef.current;
    let idx = selectionRef.current.primary;
    if (idx != null && idx < list.length) {
      const sel = list[idx];
      const s = sel.start_time ?? 0;
      const e = sel.end_time ?? totalDuration;
      if (t <= s || t >= e) idx = null;
    }
    if (idx == null) {
      idx = list.findIndex(
        (s) => t > (s.start_time ?? 0) && t < (s.end_time ?? totalDuration)
      );
    }
    if (idx >= 0) {
      handleSplitAt(idx, t);
      return;
    }
    // Playhead sits on a scene boundary (e.g. parked at t=0 right after the
    // editor opens, before the user has scrubbed/played). Splitting there is a
    // no-op, so split the selected scene at its midpoint instead of refusing —
    // the user pressed Split expecting a result.
    const selIdx = selectionRef.current.primary ?? 0;
    const sel = list[selIdx];
    if (sel) {
      const s = sel.start_time ?? 0;
      const e = sel.end_time ?? totalDuration;
      if (e > s) {
        handleSplitAt(selIdx, (s + e) / 2);
        return;
      }
    }
    addToaster({ message: text.errors.splitOutOfRange, severity: "warning" });
  }, [totalDuration, addToaster, text, handleSplitAt]);

  const mergeRange = useCallback((firstIdx, lastIdx) => {
    if (firstIdx >= lastIdx) return;
    pushHistory();
    setScenes((prev) => {
      const next = deepClone(prev);
      const first = next[firstIdx];
      const last = next[lastIdx];
      // Trajectory and speaker_segments target the original sub-range; stretching
      // them across the merged span would misalign render output, so clear them.
      const merged = {
        ...first,
        end_time: last.end_time,
        trajectory: null,
        speaker_segments: null,
      };
      next.splice(firstIdx, lastIdx - firstIdx + 1, merged);
      return next;
    });
    setSelectedSceneIndex(firstIdx);
    setSelectedSceneIndices(new Set([firstIdx]));
    addToaster({
      message: text.toaster.scenesMerged.replace("{n}", lastIdx - firstIdx + 1),
      severity: "success",
    });
  }, [pushHistory, addToaster, text]);

  const handleMergeWithNext = useCallback((idx) => {
    const i = idx ?? selectionRef.current.primary;
    if (i == null || i >= scenesRef.current.length - 1) return;
    mergeRange(i, i + 1);
  }, [mergeRange]);

  const handleMergeWithPrev = useCallback((idx) => {
    const i = idx ?? selectionRef.current.primary;
    if (i == null || i <= 0) return;
    mergeRange(i - 1, i);
  }, [mergeRange]);

  const handleMergeSelected = useCallback(() => {
    const arr = [...selectionRef.current.multi].sort((a, b) => a - b);
    if (arr.length < 2) {
      addToaster({ message: text.toaster.nothingToMerge, severity: "warning" });
      return;
    }
    if (!isContiguous(arr)) {
      addToaster({ message: text.toaster.mergeNotContiguous, severity: "warning" });
      return;
    }
    mergeRange(arr[0], arr[arr.length - 1]);
  }, [mergeRange, addToaster, text]);

  const handleToolbarMerge = useCallback(() => {
    if (selectionRef.current.multi.size > 1) { handleMergeSelected(); return; }
    // On the last scene there is no "next" to merge with, so fold into the
    // previous one — otherwise the (enabled) button would be a silent no-op.
    const i = selectionRef.current.primary;
    if (i != null && i >= scenesRef.current.length - 1) handleMergeWithPrev();
    else handleMergeWithNext();
  }, [handleMergeSelected, handleMergeWithNext, handleMergeWithPrev]);

  const canToolbarMerge = useMemo(() => {
    if (selectedSceneIndices.size > 1) return isContiguous([...selectedSceneIndices]);
    if (selectedSceneIndex == null) return false;
    return selectedSceneIndex < scenes.length - 1 || selectedSceneIndex > 0;
  }, [selectedSceneIndices, selectedSceneIndex, scenes.length]);

  // ---- Clipboard (crop) ----

  const handleCopyCrop = useCallback((idx) => {
    const i = idx ?? selectionRef.current.primary;
    const scene = scenesRef.current[i];
    if (!scene?.crop) return;
    setClipboardCrop(deepClone(scene.crop));
    addToaster({ message: text.toaster.cropCopied, severity: "success" });
  }, [addToaster, text]);

  const handlePasteCrop = useCallback((idx = null) => {
    if (!clipboardCrop) {
      addToaster({ message: text.toaster.clipboardEmpty, severity: "warning" });
      return;
    }
    const multi = selectionRef.current.multi;
    let targets;
    if (idx != null && multi.has(idx) && multi.size > 1) {
      targets = [...multi];
    } else if (idx != null) {
      targets = [idx];
    } else {
      targets = [...multi];
    }
    if (!targets.length) return;
    pushHistory();
    setScenes((prev) => {
      const next = deepClone(prev);
      targets.forEach((i) => {
        if (next[i]) {
          // Trajectory was tracked for the old crop window — invalidate so the
          // new crop drives a static frame instead of misaligned tracking.
          next[i] = { ...next[i], crop: deepClone(clipboardCrop), trajectory: null };
        }
      });
      return next;
    });
    addToaster({
      message: text.toaster.cropPasted.replace("{n}", targets.length),
      severity: "success",
    });
  }, [clipboardCrop, pushHistory, addToaster, text]);

  const handleApplyCropToAll = useCallback((idx) => {
    const i = idx ?? selectionRef.current.primary;
    const src = scenesRef.current[i];
    if (!src?.crop) return;
    const crop = deepClone(src.crop);
    pushHistory();
    setScenes((prev) =>
      prev.map((s, j) => (j === i ? s : { ...s, crop, trajectory: null }))
    );
    addToaster({ message: text.toaster.cropApplied, severity: "success" });
  }, [pushHistory, addToaster, text]);

  // ---- Reset ----

  const handleResetScene = useCallback((idx) => {
    const i = idx ?? selectionRef.current.primary;
    const cur = scenesRef.current[i];
    if (!cur || !originalScenesRef.current.length) return;
    const orig = originalScenesRef.current.find(
      (o) =>
        (o.start_time ?? 0) <= (cur.start_time ?? 0)
        && (o.end_time ?? Infinity) >= (cur.end_time ?? 0)
    );
    if (!orig) return;
    pushHistory();
    setScenes((prev) => {
      const next = deepClone(prev);
      next[i] = {
        ...cur,
        strategy: orig.strategy,
        crop: deepClone(orig.crop),
        trajectory: orig.trajectory ? deepClone(orig.trajectory) : null,
        face_track: orig.face_track ?? false,
      };
      return next;
    });
    addToaster({ message: text.toaster.sceneReset, severity: "success" });
  }, [pushHistory, addToaster, text]);

  // ---- Context menu dispatcher ----

  const handleContextAction = useCallback((action, ctx) => {
    switch (action) {
      case "split":         return handleSplitAt(ctx.sceneIndex, ctx.time);
      case "merge_prev":    return handleMergeWithPrev(ctx.sceneIndex);
      case "merge_next":    return handleMergeWithNext(ctx.sceneIndex);
      case "merge_selected":return handleMergeSelected();
      case "copy_crop":     return handleCopyCrop(ctx.sceneIndex);
      case "paste_crop":    return handlePasteCrop(ctx.sceneIndex);
      case "apply_to_all":  return handleApplyCropToAll(ctx.sceneIndex);
      case "reset":         return handleResetScene(ctx.sceneIndex);
      case "undo":          return undo();
      case "redo":          return redo();
      default: return;
    }
  }, [
    handleSplitAt, handleMergeWithPrev, handleMergeWithNext, handleMergeSelected,
    handleCopyCrop, handlePasteCrop, handleApplyCropToAll, handleResetScene, undo, redo,
  ]);

  // ---- Save & enqueue (no polling) ----

  const handleBack = useCallback(() => {
    window.history.pushState({}, "", "/content");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, []);

  const handleSave = useCallback(() => {
    setSaveDialogOpen(true);
  }, []);

  const handleConfirmSave = useCallback(async () => {
    if (overlay.enabled && !overlay.valid) {
      addToaster({ message: "Fill in all overlay fields before saving", severity: "error" });
      return;
    }

    const scenesEdited = JSON.stringify(scenes) !== JSON.stringify(originalScenesRef.current);
    const hasOverlay = overlay.enabled && overlay.payload;

    if (!scenesEdited && !hasOverlay) {
      addToaster({ message: text.toaster.nothingToSave, severity: "warning" });
      return;
    }

    setSaveDialogOpen(false);
    setSaving(true);
    try {
      if (!scenesEdited && hasOverlay) {
        await applyOverlayOnly(contentId, overlay.payload);
        addToaster({ message: text.toaster.overlayQueued, severity: "success" });
      } else {
        await updateReframeEditData(contentId, { scenes, source_video_url: sourceUrl });
        await reRenderReframe(contentId, overlay.enabled ? overlay.payload : undefined);
        addToaster({ message: text.toaster.queued, severity: "success" });
      }
    } catch (err) {
      addToaster({ message: text.errors.saveFailed, severity: "error" });
    }
    setSaving(false);
  }, [contentId, scenes, sourceUrl, overlay, updateReframeEditData, reRenderReframe, applyOverlayOnly, addToaster, text]);

  // ---- Keyboard shortcuts ----

  // Use a ref so the listener stays bound once and always sees the latest handlers.
  const shortcutsRef = useRef({});
  useEffect(() => {
    const seekTo = (t) => {
      const v = videoRef.current;
      if (!v) return;
      v.currentTime = Math.max(0, Math.min(t, totalDuration || v.duration || 0));
    };
    shortcutsRef.current = {
      undo, redo, handleCopyCrop, handlePasteCrop,
      handleSplitAtPlayhead, handleMergeWithNext, handleMergeWithPrev,
      handleMergeSelected, seekTo, shortcutsOpen, togglePlay: () => {
        const v = videoRef.current;
        if (v) v.paused ? v.play() : v.pause();
      },
      clearMulti: () => {
        const p = selectionRef.current.primary;
        setSelectedSceneIndices(new Set([p].filter((x) => x != null)));
      },
      nudgeCrop: (dx, dy) => {
        const crop = scenesRef.current[selectionRef.current.primary]?.crop;
        if (!crop) return;
        const x = Math.max(0, Math.min(crop.x + dx, sourceVideoSize.width - crop.width));
        const y = Math.max(0, Math.min(crop.y + dy, sourceVideoSize.height - crop.height));
        if (x === crop.x && y === crop.y) return;
        handleCropChange({ ...crop, x, y });
      },
      stepFrame: (dir) => {
        const v = videoRef.current;
        if (!v) return;
        if (!v.paused) v.pause();
        seekTo(v.currentTime + dir * FRAME_STEP);
      },
      stepScene: (dir) => {
        const count = scenesRef.current.length;
        if (!count) return;
        const cur = selectionRef.current.primary ?? 0;
        const next = Math.max(0, Math.min(cur + dir, count - 1));
        if (next !== cur) handleSelectScene(next);
      },
      toggleShortcutsDialog: () => setShortcutsOpen((p) => !p),
    };
  });

  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;
      // Don't hijack open Select / listbox / combobox interactions.
      if (t?.closest?.('[role="combobox"], [role="listbox"], [role="option"], .MuiSelect-select')) return;
      const h = shortcutsRef.current;
      const isMod = e.ctrlKey || e.metaKey;
      const k = e.key;
      const lk = k.toLowerCase();

      if (!isMod && (k === "?" || (k === "/" && e.shiftKey))) {
        e.preventDefault();
        h.toggleShortcutsDialog();
        return;
      }
      // The dialog owns the keyboard while it is open (Escape closes it via MUI).
      if (h.shortcutsOpen) return;

      // Cmd+Arrow is macOS line/history navigation — leave it alone. Alt+Arrow IS
      // browser back/forward on Windows/Linux, but a full-screen editor owns it.
      if (k.startsWith("Arrow") && !isMod) {
        e.preventDefault();
        const dx = k === "ArrowLeft" ? -1 : k === "ArrowRight" ? 1 : 0;
        const dy = k === "ArrowUp" ? -1 : k === "ArrowDown" ? 1 : 0;
        if (e.altKey) {
          if (dy) h.stepScene(dy);
          else h.stepFrame(dx);
        } else {
          const step = e.shiftKey ? NUDGE_FAST_PX : 1;
          h.nudgeCrop(dx * step, dy * step);
        }
        return;
      }
      if (k === "Home" || k === "End") {
        e.preventDefault();
        h.seekTo(k === "Home" ? 0 : Infinity);
        return;
      }

      if (k === " ") {
        e.preventDefault();
        h.togglePlay();
        return;
      }
      if (isMod && lk === "z" && !e.shiftKey) {
        e.preventDefault();
        h.undo();
        return;
      }
      if ((isMod && lk === "z" && e.shiftKey) || (isMod && lk === "y")) {
        e.preventDefault();
        h.redo();
        return;
      }
      if (isMod && lk === "c") {
        if (window.getSelection?.()?.toString()) return;
        e.preventDefault();
        h.handleCopyCrop();
        return;
      }
      if (isMod && lk === "v") {
        e.preventDefault();
        h.handlePasteCrop();
        return;
      }
      if (lk === "s" && !isMod && !e.altKey) {
        e.preventDefault();
        h.handleSplitAtPlayhead();
        return;
      }
      if (lk === "m" && !isMod && !e.altKey) {
        e.preventDefault();
        if (e.shiftKey) h.handleMergeWithPrev();
        else if (selectionRef.current.multi.size > 1) h.handleMergeSelected();
        else if (selectionRef.current.primary != null
                 && selectionRef.current.primary >= scenesRef.current.length - 1) {
          h.handleMergeWithPrev();   // last scene: no "next" → merge into previous
        } else h.handleMergeWithNext();
        return;
      }
      if (k === "Escape") {
        h.clearMulti();
        return;
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Suppress the browser's native context menu everywhere in the editor.
  // Scene blocks have their own onContextMenu that stops propagation, so the
  // custom right-click menu still works on scenes; other areas just go quiet.
  const handleRootContextMenu = useCallback((e) => e.preventDefault(), []);

  // ---- Render ----

  if (loading) {
    return (
      <EditorRoot>
        <LoadingOverlay>
          <CircularProgress sx={{ color: "#9b46e1" }} />
        </LoadingOverlay>
      </EditorRoot>
    );
  }

  return (
    <EditorRoot onContextMenu={handleRootContextMenu}>
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{text.toolbar.save}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {overlay.enabled && overlayTemplate ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {text.overlay.summaryTemplate}:{" "}
                <Typography component="span" variant="body2" fontWeight={600}>
                  {overlayTemplate.name}
                </Typography>
              </Typography>
              {overlayTemplate.elements
                .filter((el) => el.type === "text")
                .map((el) => (
                  <Typography key={el.id} variant="body2" color="text.secondary">
                    {el.label}:{" "}
                    <Typography component="span" variant="body2">
                      {overlay.payload?.textOverrides?.[el.id] || el.defaultText}
                    </Typography>
                  </Typography>
                ))}
              <Typography variant="body2" color="text.secondary">
                {text.overlay.summaryTiming}:{" "}
                <Typography component="span" variant="body2">
                  {(() => {
                    const t = overlay.payload?.timing;
                    if (!t || t.mode === "full") return text.overlay.timingFull;
                    if (t.mode === "fromStart") return text.overlay.timingFromStart.replace("{n}", t.end);
                    return text.overlay.timingRange.replace("{start}", t.start).replace("{end}", t.end);
                  })()}
                </Typography>
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">{text.overlay.none}</Typography>
              <Button size="small" onClick={() => { setSaveDialogOpen(false); setOverlayDrawerOpen(true); }}>
                {text.overlay.add}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} color="inherit">
            {text.toolbar.back}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSave}
            disabled={saving}
            sx={{ backgroundColor: "#9b46e1", "&:hover": { backgroundColor: "#7d35b8" } }}
          >
            {saving ? text.toolbar.saving : text.toolbar.save}
          </Button>
        </DialogActions>
      </Dialog>

      <ThemeProvider theme={darkTheme}>
        <Drawer
          anchor="right"
          variant="persistent"
          open={overlayDrawerOpen}
          onClose={() => setOverlayDrawerOpen(false)}
          PaperProps={{
            sx: {
              // On a phone the 360px drawer would take ~all of the screen — go
              // full-width so it reads as a proper overlay panel instead.
              width: isMobile ? "100%" : 360,
              maxWidth: "100%",
              backgroundColor: "#111118",
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              // EditorRoot disables selection globally; restore it for drawer inputs
              // so users see standard click-drag / double-click selection highlights.
              "& input, & textarea": {
                userSelect: "text",
                WebkitUserSelect: "text",
              },
              "& ::selection": {
                backgroundColor: "rgba(155, 70, 225, 0.45)",
                color: "#fff",
              },
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            <Typography variant="subtitle1" fontWeight={600}>{text.overlay.title}</Typography>
            <IconButton size="small" onClick={() => setOverlayDrawerOpen(false)} sx={{ color: "rgba(255,255,255,0.6)" }}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
            <OverlayPicker value={overlay} onChange={setOverlay} />
          </Box>
          <Box sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setOverlayDrawerOpen(false)}
              disabled={overlay.enabled && !overlay.valid}
              sx={{ backgroundColor: "#9b46e1", "&:hover": { backgroundColor: "#7d35b8" } }}
            >
              {text.overlay.save}
            </Button>
          </Box>
        </Drawer>
      </ThemeProvider>

      {noEditData && (
        <Alert
          severity="info"
          onClose={() => setNoEditData(false)}
          sx={{
            backgroundColor: 'rgba(30,30,50,0.95)',
            color: 'rgba(255,255,255,0.85)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 0,
            '& .MuiAlert-icon': { color: '#9b46e1' },
            '& .MuiAlert-action .MuiIconButton-root': { color: 'rgba(255,255,255,0.5)' },
          }}
        >
          No existing reframe data found — starting from scratch. Edit the frame and save to create it.
        </Alert>
      )}
      <EditorToolbar
        selectedScene={selectedScene}
        selectionSize={selectedSceneIndices.size}
        onBack={handleBack}
        onSplit={handleSplitAtPlayhead}
        onMerge={handleToolbarMerge}
        canMerge={canToolbarMerge}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        onSave={handleSave}
        saving={saving}
        clipboardHasCrop={!!clipboardCrop}
        onOverlay={() => setOverlayDrawerOpen(true)}
        overlayChip={overlay.enabled && overlayTemplate ? overlayTemplate.name : null}
        onShortcuts={() => setShortcutsOpen(true)}
      />

      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      <MainArea
        sx={{
          justifyContent: overlayDrawerOpen ? "center" : "flex-start",
          // The full-width mobile drawer overlays the editor, so don't reserve
          // 360px of padding for it there.
          paddingRight: !isMobile && overlayDrawerOpen ? "360px" : 0,
          transition: "padding-right 200ms ease",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: overlayDrawerOpen ? "none" : "flex",
          }}
        >
          <VideoCanvas
            videoRef={videoRef}
            sourceUrl={sourceUrl}
            cropRect={cropRect}
            onCropChange={handleCropChange}
            selectedSceneIndex={selectedSceneIndex}
            scenes={scenes}
            onDurationLoad={setTotalDuration}
            onSourceSizeLoad={handleSourceSizeLoad}
            sourceVideoSize={sourceVideoSize}
          />
        </Box>

        <VerticalPreview
          videoRef={videoRef}
          cropRect={cropRect}
          sourceVideoSize={sourceVideoSize}
          scenes={scenes}
          overlay={overlay}
          template={overlayTemplate}
          expanded={overlayDrawerOpen}
        />
      </MainArea>

      <SceneTimeline
        scenes={scenes}
        selectedSceneIndex={selectedSceneIndex}
        selectedSceneIndices={selectedSceneIndices}
        onSelectScene={handleSelectScene}
        onContextAction={handleContextAction}
        clipboardHasCrop={!!clipboardCrop}
        canUndo={canUndo}
        canRedo={canRedo}
        videoRef={videoRef}
        totalDuration={totalDuration}
      />
    </EditorRoot>
  );
}
